
import { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription,
  CardFooter
} from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { 
  Recycle, 
  Trash, 
  ArrowUp, 
  ArrowDown, 
  UtensilsCrossed, 
  DollarSign, 
  Clock, 
  ArrowRight, 
  FileText,
  Package,
  Search,
  Filter
} from 'lucide-react';

// Mock data
const wasteData = [
  { id: 1, ingredient: 'Tomates Canarios', quantity: '5.2 kg', cost: '$15.60', wastePercentage: 8.5, category: 'Produce' },
  { id: 2, ingredient: 'Carne de Cabra', quantity: '3.8 kg', cost: '$34.20', wastePercentage: 6.2, category: 'Protein' },
  { id: 3, ingredient: 'Lechuga', quantity: '2.5 kg', cost: '$7.50', wastePercentage: 12.4, category: 'Produce' },
  { id: 4, ingredient: 'Nata Montada', quantity: '1.7 L', cost: '$12.75', wastePercentage: 9.1, category: 'Dairy' },
  { id: 5, ingredient: 'Cebollas', quantity: '3.0 kg', cost: '$6.00', wastePercentage: 7.8, category: 'Produce' },
  { id: 6, ingredient: 'Carne Picada', quantity: '2.2 kg', cost: '$26.40', wastePercentage: 5.3, category: 'Protein' },
  { id: 7, ingredient: 'Pimientos', quantity: '1.8 kg', cost: '$9.00', wastePercentage: 10.2, category: 'Produce' },
  { id: 8, ingredient: 'Queso Curado', quantity: '0.9 kg', cost: '$13.50', wastePercentage: 4.6, category: 'Dairy' },
];

const recipeWasteData = [
  { id: 1, name: 'Conejo al Salmorejo', totalWaste: '$42.80', wastePercentage: 14.3 },
  { id: 2, name: 'Ensalada de Palmitos', totalWaste: '$28.50', wastePercentage: 12.1 },
  { id: 3, name: 'Ropa Vieja Canaria', totalWaste: '$35.60', wastePercentage: 9.8 },
  { id: 4, name: 'Risotto de Gofio', totalWaste: '$22.40', wastePercentage: 8.5 },
  { id: 5, name: 'Papas Arrugadas con Mojo', totalWaste: '$12.30', wastePercentage: 7.2 },
];

const crossUtilizationData = [
  { id: 1, ingredient: 'Tomates Canarios', currentUsage: '3 recipes', recommendation: 'Add to 2 more dishes', savings: '$12.40/week' },
  { id: 2, ingredient: 'Caldo de Pescado', currentUsage: '2 recipes', recommendation: 'Use in sauces & soups', savings: '$18.80/week' },
  { id: 3, ingredient: 'Cilantro', currentUsage: '1 recipe', recommendation: 'Add to 3 more dishes', savings: '$8.60/week' },
  { id: 4, ingredient: 'Jugo de Limón', currentUsage: '4 recipes', recommendation: 'Use in dressings', savings: '$5.20/week' },
];

const prepScheduleData = [
  { id: 1, item: 'Preparación de Cabra', currentSchedule: 'Daily', recommendation: 'Prep 3x week', reasoning: 'Low variability in daily usage' },
  { id: 2, item: 'Verduras Picadas', currentSchedule: 'Morning only', recommendation: 'Split AM/PM prep', reasoning: 'High dinner demand' },
  { id: 3, item: 'Postres', currentSchedule: 'Every 2 days', recommendation: 'Based on weekend forecast', reasoning: '65% higher weekend consumption' },
];

// Charts data
const wasteByCategory = [
  { name: 'Produce', value: 38 },
  { name: 'Protein', value: 27 },
  { name: 'Dairy', value: 15 },
  { name: 'Grains', value: 12 },
  { name: 'Oils', value: 8 },
];

const financialImpactData = [
  { name: 'Current Waste', value: 1250 },
  { name: 'Potential Savings', value: 875 },
  { name: 'Target', value: 375 },
];

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#e91e63'];

const WasteReductionAnalyzer = () => {
  const [activeFilter, setActiveFilter] = useState('all');

  return (
    <div className="space-y-8 animate-fade-in">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Waste Reduction Analyzer</h1>
          <p className="text-muted-foreground mt-1">Track, analyze, and reduce food waste</p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input 
              placeholder="Search ingredients..." 
              className="pl-9 w-full sm:w-[250px]"
            />
          </div>
          <Button variant="outline" className="flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </Button>
        </div>
      </div>

      {/* Analytics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Total Waste Cost</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex justify-between items-center">
              <span className="text-3xl font-bold">$1,250</span>
              <span className="flex items-center text-red-500">
                <ArrowUp className="mr-1 h-4 w-4" />
                8.3%
              </span>
            </div>
            <p className="text-sm text-muted-foreground mt-2">vs. previous month</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Waste by Weight</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex justify-between items-center">
              <span className="text-3xl font-bold">48.6kg</span>
              <span className="flex items-center text-red-500">
                <ArrowUp className="mr-1 h-4 w-4" />
                5.2%
              </span>
            </div>
            <p className="text-sm text-muted-foreground mt-2">vs. previous month</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Avg. Recipe Waste</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex justify-between items-center">
              <span className="text-3xl font-bold">9.6%</span>
              <span className="flex items-center text-green-500">
                <ArrowDown className="mr-1 h-4 w-4" />
                2.1%
              </span>
            </div>
            <p className="text-sm text-muted-foreground mt-2">vs. previous month</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Potential Savings</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex justify-between items-center">
              <span className="text-3xl font-bold">$875</span>
              <span className="flex items-center text-green-500">
                <ArrowUp className="mr-1 h-4 w-4" />
                70%
              </span>
            </div>
            <p className="text-sm text-muted-foreground mt-2">reduction opportunity</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="waste-tracking" className="w-full">
        <TabsList className="grid grid-cols-2 md:grid-cols-5 w-full">
          <TabsTrigger value="waste-tracking">Waste Tracking</TabsTrigger>
          <TabsTrigger value="recipe-analysis">Recipe Analysis</TabsTrigger>
          <TabsTrigger value="cross-utilization">Cross-Utilization</TabsTrigger>
          <TabsTrigger value="financial-impact">Financial Impact</TabsTrigger>
          <TabsTrigger value="prep-schedule">Prep Schedule</TabsTrigger>
        </TabsList>
        
        {/* Waste Tracking Tab */}
        <TabsContent value="waste-tracking" className="mt-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle>Ingredient Waste Tracking</CardTitle>
                <CardDescription>Track ingredient waste through POS integration</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Ingredient</TableHead>
                        <TableHead>Waste Quantity</TableHead>
                        <TableHead>Cost</TableHead>
                        <TableHead>Waste %</TableHead>
                        <TableHead>Category</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {wasteData.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell className="font-medium">{item.ingredient}</TableCell>
                          <TableCell>{item.quantity}</TableCell>
                          <TableCell>{item.cost}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span className={item.wastePercentage > 10 ? "text-red-500" : "text-amber-500"}>
                                {item.wastePercentage}%
                              </span>
                              <Progress 
                                value={item.wastePercentage * 10} 
                                className="h-2 w-20"
                                // Using inline styles instead of indicatorClassName
                                style={{ 
                                  backgroundColor: item.wastePercentage > 10 ? 'rgba(239, 68, 68, 0.2)' : 'rgba(245, 158, 11, 0.2)' 
                                }}
                              />
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{item.category}</Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Waste by Category</CardTitle>
                <CardDescription>Distribution of waste by ingredient category</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={wasteByCategory}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        animationDuration={1500}
                      >
                        {wasteByCategory.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                          border: 'none'
                        }}
                        formatter={(value) => [`${value}%`, 'Percentage']}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
          
          <Card>
            <CardHeader>
              <CardTitle>Waste Trends</CardTitle>
              <CardDescription>Monthly waste trends over the past 6 months</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={[
                      { month: 'Jan', produce: 42, protein: 28, dairy: 18, grains: 15 },
                      { month: 'Feb', produce: 38, protein: 30, dairy: 15, grains: 12 },
                      { month: 'Mar', produce: 40, protein: 27, dairy: 17, grains: 14 },
                      { month: 'Apr', produce: 35, protein: 25, dairy: 14, grains: 10 },
                      { month: 'May', produce: 38, protein: 27, dairy: 16, grains: 12 },
                      { month: 'Jun', produce: 40, protein: 28, dairy: 15, grains: 13 },
                    ]}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip 
                      contentStyle={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        border: 'none'
                      }}
                    />
                    <Legend />
                    <Bar dataKey="produce" name="Produce" fill="#0088FE" />
                    <Bar dataKey="protein" name="Protein" fill="#00C49F" />
                    <Bar dataKey="dairy" name="Dairy" fill="#FFBB28" />
                    <Bar dataKey="grains" name="Grains" fill="#FF8042" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Recipe Analysis Tab */}
        <TabsContent value="recipe-analysis" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Recipes with Highest Waste</CardTitle>
              <CardDescription>Identify recipes contributing the most to waste</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Recipe Name</TableHead>
                      <TableHead>Total Waste Cost</TableHead>
                      <TableHead>Waste %</TableHead>
                      <TableHead>Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recipeWasteData.map((recipe) => (
                      <TableRow key={recipe.id}>
                        <TableCell className="font-medium">{recipe.name}</TableCell>
                        <TableCell>{recipe.totalWaste}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className={recipe.wastePercentage > 10 ? "text-red-500" : "text-amber-500"}>
                              {recipe.wastePercentage}%
                            </span>
                            <Progress 
                              value={recipe.wastePercentage * 10} 
                              className="h-2 w-20"
                              // Using inline styles instead of indicatorClassName
                              style={{ 
                                backgroundColor: recipe.wastePercentage > 10 ? 'rgba(239, 68, 68, 0.2)' : 'rgba(245, 158, 11, 0.2)' 
                              }}
                            />
                          </div>
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm">View</Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <div className="space-y-1">
                  <CardTitle>Chicken Parmesan</CardTitle>
                  <CardDescription>Detailed waste analysis</CardDescription>
                </div>
                <UtensilsCrossed className="h-5 w-5 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-muted-foreground">Chicken Breast</span>
                      <span className="font-medium">8.2% waste</span>
                    </div>
                    <Progress value={82} className="h-2" />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-muted-foreground">Tomato Sauce</span>
                      <span className="font-medium">14.5% waste</span>
                    </div>
                    <Progress value={145} className="h-2" />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-muted-foreground">Mozzarella</span>
                      <span className="font-medium">6.8% waste</span>
                    </div>
                    <Progress value={68} className="h-2" />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-muted-foreground">Breadcrumbs</span>
                      <span className="font-medium">21.3% waste</span>
                    </div>
                    <Progress value={213} className="h-2" />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-muted-foreground">Fresh Herbs</span>
                      <span className="font-medium">18.6% waste</span>
                    </div>
                    <Progress value={186} className="h-2" />
                  </div>
                </div>
              </CardContent>
              <CardFooter>
                <Button variant="outline" className="w-full">View Recipe Details</Button>
              </CardFooter>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Waste Reduction Recommendations</CardTitle>
                <CardDescription>How to reduce waste in high-waste recipes</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-primary/10 p-4 rounded-lg">
                  <div className="flex gap-3">
                    <div className="bg-primary/20 p-2 rounded-full h-fit">
                      <Recycle className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-medium">Breadcrumbs Utilization</h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        Save excess breadcrumbs for use in meatballs and stuffings. 
                        Consider switching to panko which has longer shelf life.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-primary/10 p-4 rounded-lg">
                  <div className="flex gap-3">
                    <div className="bg-primary/20 p-2 rounded-full h-fit">
                      <Recycle className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-medium">Fresh Herbs Preservation</h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        Freeze leftover herbs in oil in ice cube trays for future use. 
                        Implement just-in-time prep for herbs.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-primary/10 p-4 rounded-lg">
                  <div className="flex gap-3">
                    <div className="bg-primary/20 p-2 rounded-full h-fit">
                      <Recycle className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-medium">Tomato Sauce Repurposing</h4>
                      <p className="text-sm text-muted-foreground mt-1">
                        Repurpose excess tomato sauce for pizza bases or as a soup base 
                        the next day. Freeze in portioned containers.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
              <CardFooter>
                <Button className="w-full">Apply Recommendations</Button>
              </CardFooter>
            </Card>
          </div>
        </TabsContent>
        
        {/* Cross-Utilization Tab */}
        <TabsContent value="cross-utilization" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Ingredient Cross-Utilization Opportunities</CardTitle>
              <CardDescription>
                Suggestions for using ingredients across multiple dishes
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Ingredient</TableHead>
                      <TableHead>Current Usage</TableHead>
                      <TableHead>Recommendation</TableHead>
                      <TableHead>Potential Savings</TableHead>
                      <TableHead>Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {crossUtilizationData.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="font-medium">{item.ingredient}</TableCell>
                        <TableCell>{item.currentUsage}</TableCell>
                        <TableCell>{item.recommendation}</TableCell>
                        <TableCell className="text-green-600">{item.savings}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" className="flex items-center gap-1">
                            View Recipes
                            <ArrowRight className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="hover-scale">
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <CardTitle>Citrus Zest</CardTitle>
                  <div className="bg-primary/10 p-2 rounded-full">
                    <Package className="h-5 w-5 text-primary" />
                  </div>
                </div>
                <CardDescription>High waste ingredient</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium">Current Usage:</p>
                    <ul className="text-sm text-muted-foreground ml-5 list-disc">
                      <li>Dessert garnish only</li>
                    </ul>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Suggested Applications:</p>
                    <ul className="text-sm text-muted-foreground ml-5 list-disc">
                      <li>Fish marinades</li>
                      <li>Vinaigrette dressings</li>
                      <li>Infused oils</li>
                      <li>Compound butters</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
              <CardFooter>
                <p className="text-sm text-green-600 font-medium">
                  Potential savings: $7.80/week
                </p>
              </CardFooter>
            </Card>
            
            <Card className="hover-scale">
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <CardTitle>Herb Stems</CardTitle>
                  <div className="bg-primary/10 p-2 rounded-full">
                    <Package className="h-5 w-5 text-primary" />
                  </div>
                </div>
                <CardDescription>High waste ingredient</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium">Current Usage:</p>
                    <ul className="text-sm text-muted-foreground ml-5 list-disc">
                      <li>Discarded during prep</li>
                    </ul>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Suggested Applications:</p>
                    <ul className="text-sm text-muted-foreground ml-5 list-disc">
                      <li>Stocks and broths</li>
                      <li>Infused oils</li>
                      <li>Chimichurri sauce</li>
                      <li>Herb salt blends</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
              <CardFooter>
                <p className="text-sm text-green-600 font-medium">
                  Potential savings: $10.40/week
                </p>
              </CardFooter>
            </Card>
            
            <Card className="hover-scale">
              <CardHeader className="pb-2">
                <div className="flex justify-between items-start">
                  <CardTitle>Day-Old Bread</CardTitle>
                  <div className="bg-primary/10 p-2 rounded-full">
                    <Package className="h-5 w-5 text-primary" />
                  </div>
                </div>
                <CardDescription>High waste ingredient</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div>
                    <p className="text-sm font-medium">Current Usage:</p>
                    <ul className="text-sm text-muted-foreground ml-5 list-disc">
                      <li>Limited to croutons</li>
                    </ul>
                  </div>
                  <div>
                    <p className="text-sm font-medium">Suggested Applications:</p>
                    <ul className="text-sm text-muted-foreground ml-5 list-disc">
                      <li>Bread pudding</li>
                      <li>Panzanella salad</li>
                      <li>French toast</li>
                      <li>Breadcrumbs for coating</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
              <CardFooter>
                <p className="text-sm text-green-600 font-medium">
                  Potential savings: $15.20/week
                </p>
              </CardFooter>
            </Card>
          </div>
        </TabsContent>
        
        {/* Financial Impact Tab */}
        <TabsContent value="financial-impact" className="mt-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Financial Impact Overview</CardTitle>
                <CardDescription>
                  Breakdown of current waste costs and potential savings
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={financialImpactData}
                      margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'white',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                          border: 'none'
                        }}
                        formatter={(value) => [`$${value}`, 'Amount']}
                      />
                      <Legend />
                      <Bar 
                        dataKey="value" 
                        name="Amount ($)" 
                        fill="#3b82f6" 
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Waste Reduction ROI</CardTitle>
                <CardDescription>
                  Impact of implementing waste reduction strategies
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-1">
                  <div className="flex justify-between">
                    <span className="text-sm">Implementation Cost</span>
                    <span className="font-medium">$2,400</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm">Monthly Savings</span>
                    <span className="font-medium text-green-600">$875</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm">ROI Period</span>
                    <span className="font-medium">2.7 months</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-sm">Annual Savings</span>
                    <span className="font-medium text-green-600">$10,500</span>
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <h4 className="font-medium mb-2">
                    <DollarSign className="h-4 w-4 inline mr-1" />
                    Financial Benefits
                  </h4>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex gap-2">
                      <ArrowRight className="h-4 w-4 shrink-0 text-green-500" />
                      Reduced food costs by 12% annually
                    </li>
                    <li className="flex gap-2">
                      <ArrowRight className="h-4 w-4 shrink-0 text-green-500" />
                      Labor savings from optimized prep: 5 hours/week
                    </li>
                    <li className="flex gap-2">
                      <ArrowRight className="h-4 w-4 shrink-0 text-green-500" />
                      Waste disposal cost reduction: $180/month
                    </li>
                    <li className="flex gap-2">
                      <ArrowRight className="h-4 w-4 shrink-0 text-green-500" />
                      Improved inventory turnover by 18%
                    </li>
                  </ul>
                </div>
              </CardContent>
              <CardFooter>
                <Button className="w-full">Generate Detailed Report</Button>
              </CardFooter>
            </Card>
          </div>
          
          <Card>
            <CardHeader>
              <CardTitle>Waste Reduction Initiatives Impact</CardTitle>
              <CardDescription>Financial impact of specific waste reduction strategies</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Initiative</TableHead>
                      <TableHead>Implementation Cost</TableHead>
                      <TableHead>Monthly Savings</TableHead>
                      <TableHead>ROI Period</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-medium">Portion Control System</TableCell>
                      <TableCell>$1,200</TableCell>
                      <TableCell className="text-green-600">$420</TableCell>
                      <TableCell>2.9 months</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-amber-500/10 text-amber-500 border-amber-500/20">
                          In Progress
                        </Badge>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Cross-Utilization Program</TableCell>
                      <TableCell>$200</TableCell>
                      <TableCell className="text-green-600">$280</TableCell>
                      <TableCell>0.7 months</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/20">
                          Implemented
                        </Badge>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Staff Training</TableCell>
                      <TableCell>$800</TableCell>
                      <TableCell className="text-green-600">$340</TableCell>
                      <TableCell>2.4 months</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/20">
                          Implemented
                        </Badge>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium">Digital Inventory System</TableCell>
                      <TableCell>$1,600</TableCell>
                      <TableCell className="text-green-600">$380</TableCell>
                      <TableCell>4.2 months</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-blue-500/10 text-blue-500 border-blue-500/20">
                          Planned
                        </Badge>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Prep Schedule Tab */}
        <TabsContent value="prep-schedule" className="mt-6 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Prep Schedule Recommendations</CardTitle>
              <CardDescription>
                Recommended adjustments based on sales forecasts and ingredient usage
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Prep Item</TableHead>
                      <TableHead>Current Schedule</TableHead>
                      <TableHead>Recommended Schedule</TableHead>
                      <TableHead>Reasoning</TableHead>
                      <TableHead>Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {prepScheduleData.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell className="font-medium">{item.item}</TableCell>
                        <TableCell>{item.currentSchedule}</TableCell>
                        <TableCell>{item.recommendation}</TableCell>
                        <TableCell>{item.reasoning}</TableCell>
                        <TableCell>
                          <Button variant="outline" size="sm">
                            Apply
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Sales Forecast Impact</CardTitle>
                <CardDescription>
                  How sales forecasts inform optimal prep scheduling
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="bg-secondary/30 p-4 rounded-lg">
                    <div className="flex gap-3">
                      <div className="bg-secondary p-2 rounded-full h-fit">
                        <Clock className="h-5 w-5 text-secondary-foreground" />
                      </div>
                      <div>
                        <h4 className="font-medium">Weekend Volume Spike</h4>
                        <p className="text-sm text-muted-foreground mt-1">
                          Sales data shows 65% higher volume on weekends. 
                          Adjust prep schedule to peak on Friday afternoon instead of Saturday morning.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-secondary/30 p-4 rounded-lg">
                    <div className="flex gap-3">
                      <div className="bg-secondary p-2 rounded-full h-fit">
                        <Clock className="h-5 w-5 text-secondary-foreground" />
                      </div>
                      <div>
                        <h4 className="font-medium">Mid-Week Lull</h4>
                        <p className="text-sm text-muted-foreground mt-1">
                          Tuesday and Wednesday show 40% lower sales. 
                          Reduce prep by 30% on these days and focus on ingredient cross-utilization.
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-secondary/30 p-4 rounded-lg">
                    <div className="flex gap-3">
                      <div className="bg-secondary p-2 rounded-full h-fit">
                        <Clock className="h-5 w-5 text-secondary-foreground" />
                      </div>
                      <div>
                        <h4 className="font-medium">Happy Hour Impact</h4>
                        <p className="text-sm text-muted-foreground mt-1">
                          Happy hour (4-6pm) increases appetizer orders by 85%. 
                          Schedule dedicated appetizer prep 2-3pm daily.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader>
                <CardTitle>Optimized Schedule Generator</CardTitle>
                <CardDescription>
                  Create prep schedules based on forecast and waste reduction goals
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="date-range">Date Range</Label>
                    <Select defaultValue="next-week">
                      <SelectTrigger id="date-range">
                        <SelectValue placeholder="Select date range" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="this-week">This week</SelectItem>
                        <SelectItem value="next-week">Next week</SelectItem>
                        <SelectItem value="two-weeks">Next 2 weeks</SelectItem>
                        <SelectItem value="month">Next month</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="staff-level">Staffing Level</Label>
                    <Select defaultValue="normal">
                      <SelectTrigger id="staff-level">
                        <SelectValue placeholder="Select staffing" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="reduced">Reduced</SelectItem>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="peak">Peak Season</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="waste-target">Waste Reduction Target</Label>
                  <Select defaultValue="balanced">
                    <SelectTrigger id="waste-target">
                      <SelectValue placeholder="Select target" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="aggressive">Aggressive (30%+)</SelectItem>
                      <SelectItem value="balanced">Balanced (15-20%)</SelectItem>
                      <SelectItem value="conservative">Conservative (5-10%)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="forecast-data">Sales Forecast Source</Label>
                  <Select defaultValue="pos-last-month">
                    <SelectTrigger id="forecast-data">
                      <SelectValue placeholder="Select source" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pos-last-month">POS Data (Last Month)</SelectItem>
                      <SelectItem value="ai-prediction">AI Prediction Model</SelectItem>
                      <SelectItem value="manual">Manual Entry</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
              <CardFooter>
                <Button className="w-full">Generate Optimized Schedule</Button>
              </CardFooter>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default WasteReductionAnalyzer;
